import { Alert, Button, Drawer, Form, Grid, Input, InputNumber, Select, Space } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { classKeys, useCourses, type AcademicYear, type ClassDetails } from "../app/classes.ts";

interface Props {
  open: boolean;
  year: AcademicYear | undefined;
  onClose(): void;
  onCreated(created: ClassDetails): void;
}

interface ClassForm {
  name: string;
  grade: number;
  courseId: string;
}

/** Creates a class: name, grade and one course offered for that grade (decision 12). */
export function ClassDrawer({ open, year, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ClassForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const courses = useCourses();
  const grade = Form.useWatch("grade", form);

  const mutation = useMutation({
    mutationFn: async (values: ClassForm) => {
      const { data, error, response } = await api.POST("/academic/classes", {
        body: { academicYearId: year?.id ?? null, name: values.name, grade: values.grade, courseId: values.courseId },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: classKeys.all });
      form.resetFields();
      setSummary([]);
      onCreated(created);
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const courseOptions = (courses.data ?? [])
    .filter((c) => grade === undefined || c.allowedGrades.map(Number).includes(Number(grade)))
    .map((c) => ({ value: c.id, label: c.name }));

  return (
    <Drawer
      title={t("classes.create")}
      open={open}
      onClose={onClose}
      size={screens.md ? 480 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("family.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("family.save")}
          </Button>
        </Space>
      }
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Form<ClassForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values)}>
        <Form.Item label={t("classes.year")}>
          <Input value={year?.label ?? ""} disabled aria-label={t("classes.year")} />
        </Form.Item>
        <Form.Item name="grade" label={t("classes.grade")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <InputNumber min={5} max={12} style={{ width: "100%" }} onChange={() => form.setFieldValue("courseId", undefined)} />
        </Form.Item>
        <Form.Item name="name" label={t("classes.name")} extra={t("classes.nameHint")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <Input maxLength={16} />
        </Form.Item>
        <Form.Item name="courseId" label={t("classes.course")} extra={t("classes.courseHint")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <Select options={courseOptions} loading={courses.isPending} showSearch={{ optionFilterProp: "label" }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
